import { useEffect } from "react";

type ScreenWakeLock = {
  release: () => Promise<void>;
  addEventListener: (type: "release", listener: () => void) => void;
};

/**
 * Keep the screen awake while uploads are in progress and the tab is visible.
 * Browsers release the lock when the page is hidden; we re-request on return.
 */
export function useWakeLock(active: boolean): void {
  useEffect(() => {
    if (!active || typeof navigator === "undefined" || !("wakeLock" in navigator)) {
      return;
    }

    let sentinel: ScreenWakeLock | null = null;
    let cancelled = false;

    const request = async () => {
      if (cancelled || document.visibilityState !== "visible") return;
      try {
        sentinel = await navigator.wakeLock.request("screen");
        sentinel.addEventListener("release", () => {
          if (!cancelled && document.visibilityState === "visible" && active) {
            void request();
          }
        });
      } catch {
        // Wake Lock is optional; iOS may deny it when battery saver is on.
      }
    };

    void request();

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void request();
      }
    };

    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      void sentinel?.release();
      sentinel = null;
    };
  }, [active]);
}
